import clsx from 'clsx';
import { FC } from 'react';
import {
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  Pagination as ShadUIPagination,
} from '../ui/pagination';

interface IPaginationProps extends React.ComponentProps<'nav'> {
  page?: number;
  onPageChange: (page: number) => void;
  total?: number;
  limit?: number;
  totalPage?: number;
}

const Pagination: FC<IPaginationProps> = ({
  onPageChange,
  page = 1,
  total = 0,
  limit = 1,
  totalPage: totalPageProps,
  ...props
}) => {
  const totalPage = totalPageProps || Math.floor(total / limit) + 1;

  const handleBack = () => {
    if (page <= 1) return;
    onPageChange(page - 1);
  };

  const handleNext = () => {
    if (page >= totalPage) return;
    onPageChange(page + 1);
  };

  const handleChangePage = (selectedPage: number) => {
    if (selectedPage === page) return;
    onPageChange(selectedPage);
  };

  return (
    <ShadUIPagination {...props}>
      <PaginationContent>
        <PaginationItem onClick={handleBack}>
          <PaginationPrevious
            className={clsx(
              'p-1 bg-white cursor-pointer w-7 h-7',
              page === 1
                ? 'text-gray-400 hover:bg-transparent hover:text-gray-400'
                : 'text-black hover:bg-white',
            )}
          />
        </PaginationItem>
        {generatePagination(page, totalPage).map((p, index) =>
          p === '...' ? (
            <PaginationItem key={`ellipsis-${index}`}>
              <PaginationEllipsis />
            </PaginationItem>
          ) : (
            <PaginationItem key={p} onClick={() => handleChangePage(+p)}>
              <PaginationLink className='w-7 h-7 cursor-pointer' isActive={page === +p}>
                {+p}
              </PaginationLink>
            </PaginationItem>
          ),
        )}
        <PaginationItem onClick={handleNext}>
          <PaginationNext
            className={clsx(
              'p-1 bg-white cursor-pointer w-7 h-7',
              page === totalPage
                ? 'text-gray-400 hover:bg-transparent hover:text-gray-400'
                : 'text-black',
            )}
          />
        </PaginationItem>
      </PaginationContent>
    </ShadUIPagination>
  );
};

function generatePagination(currentPage: number, totalPages: number, displayRange = 1) {
  const result: (string | number)[] = [];

  if (totalPages <= 1) return [1]; // Only one page exists

  // Add first page
  result.push(1);

  // Add leading ellipsis if necessary
  if (currentPage > displayRange + 2) {
    result.push('...');
  }

  // Add page numbers around the current page
  const start = Math.max(2, currentPage - displayRange);
  const end = Math.min(totalPages - 1, currentPage + displayRange);

  for (let i = start; i <= end; i++) {
    result.push(i);
  }

  // Add trailing ellipsis if necessary
  if (currentPage < totalPages - displayRange - 1) {
    result.push('...');
  }

  // Add last page
  result.push(totalPages);

  return result;
}

export default Pagination;
